/**
 * "Continue with Google" - Google Identity Services -> ID token ->
 * POST /auth/google -> session.
 *
 * The Google Cloud project is not set up yet, so REACT_APP_GOOGLE_CLIENT_ID is
 * expected to be empty for now. Everything here is written around that: with no
 * client id the component renders an inert, visibly-disabled button and never
 * touches Google's SDK. It must never be the reason the login page fails to
 * render, so the provider is mounted here rather than around the whole app -
 * any trouble it causes is confined to this corner of the page.
 */

import React, { FC, useState } from 'react';
import { CredentialResponse, GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { toast } from 'react-toastify';

import googleSvg from 'images/Google.svg';
import { GOOGLE_CLIENT_ID, GOOGLE_SIGN_IN_ENABLED } from 'utils/featureFlags';
import { authErrorMessage, googleSignIn } from 'service/authApi';
import useAuthSession from 'hooks/useAuthSession';

export interface GoogleSignInButtonProps {
    className?: string;
    /** Only changes the wording Google renders inside its own button. */
    text?: 'signin_with' | 'signup_with' | 'continue_with';
}

/**
 * Stand-in for the real thing while the Google client id is missing. Same
 * shape and weight as the buttons around it, plainly not clickable, and it
 * says why rather than leaving people prodding at a dead control.
 */
const UnconfiguredGoogleButton: FC = () => (
    <div>
        <button
            type="button"
            disabled
            aria-disabled="true"
            className="flex w-full items-center justify-center rounded-full border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-medium text-neutral-400 cursor-not-allowed dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-500"
        >
            <img className="flex-shrink-0 mr-3 h-5 w-5 opacity-40" src={googleSvg} alt="" aria-hidden="true" />
            Continue with Google
        </button>
        <span className="mt-2 block text-center text-xs text-neutral-400 dark:text-neutral-500">
            Google sign-in is coming soon. Please use a one-time code for now.
        </span>
    </div>
);

const GoogleSignInButton: FC<GoogleSignInButtonProps> = ({ className = '', text = 'continue_with' }) => {
    const establishSession = useAuthSession();
    const [busy, setBusy] = useState(false);

    if (!GOOGLE_SIGN_IN_ENABLED) {
        return (
            <div className={className}>
                <UnconfiguredGoogleButton />
            </div>
        );
    }

    const onCredential = async (credentialResponse: CredentialResponse) => {
        const idToken = credentialResponse?.credential;

        if (!idToken) {
            toast.error('Google did not return a sign-in token. Please try again.');
            return;
        }

        setBusy(true);
        try {
            const session = await googleSignIn(idToken);
            toast.success(session?.message || 'Login Success');
            await establishSession(session);
        } catch (err) {
            toast.error(authErrorMessage(err, 'We could not sign you in with Google. Please try again.'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className={className}>
            <GoogleOAuthProvider
                clientId={GOOGLE_CLIENT_ID}
                onScriptLoadError={() => {
                    // Blocked script, offline, or a network hiccup. The page and
                    // the OTP flow beside it carry on regardless.
                    console.warn('Google sign-in: the Google Identity Services script failed to load.');
                }}
            >
                {/*
                 * Google renders this button itself inside an iframe, so it
                 * cannot be styled with Tailwind - the wrapper handles width
                 * and the `pill` shape lines it up with our rounded buttons.
                 */}
                <div className={`flex justify-center ${busy ? 'pointer-events-none opacity-60' : ''}`}>
                    <GoogleLogin
                        onSuccess={onCredential}
                        onError={() => toast.error('We could not sign you in with Google. Please try again.')}
                        text={text}
                        shape="pill"
                        size="large"
                        width="384"
                        logo_alignment="center"
                    />
                </div>
            </GoogleOAuthProvider>
        </div>
    );
};

export default GoogleSignInButton;
